export const timeActivities = { service: 'Service work', testing: 'Testing', qc: 'Quality control', rework: 'Rework' }
export const isTimeUuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
const only = (o, fields) => o && typeof o === 'object' && !Array.isArray(o) && Object.keys(o).every(k => fields.includes(k))
const stamp = s => typeof s === 'string' && /(Z|[+-]\d{2}:\d{2})$/.test(s) && Number.isFinite(Date.parse(s))
const common = ['id', 'workshop_id', 'job_id', 'technician_id', 'assigned_technician_id', 'service_ref', 'activity', 'started_at', 'actor_id']

export function verifyTimeResponse(data, { workshopId, jobId, actorId, owner, writing = false }) {
  const fail = () => { throw new Error('Complete time response could not be verified.') }
  if (writing) {
    if (!only(data, ['session_id', 'entry_id', 'entry_ids', 'state']) || !Object.keys(data).length) fail()
    for (const k of ['session_id', 'entry_id']) if (data[k] !== undefined && !isTimeUuid(data[k])) fail()
    if (data.entry_ids !== undefined && (!Array.isArray(data.entry_ids) || !data.entry_ids.every(isTimeUuid))) fail()
    if (data.state !== undefined && !['final', 'pending_review'].includes(data.state)) fail()
    return data
  }
  if (!only(data, ['version', 'workshop_id', 'job_id', 'job_context', 'server_now', 'terminal', 'services', 'people', 'active', 'entries', 'corrections'])
    || data.version !== 'job-time-v1' || data.workshop_id !== workshopId || data.job_id !== jobId
    || !stamp(data.server_now) || typeof data.terminal !== 'boolean') fail()
  const context = data.job_context
  if (!only(context, ['id', 'job_number', 'module_key', 'service_summary']) || context.id !== jobId
    || !['general', 'suspension'].includes(context.module_key)
    || !['job_number', 'service_summary'].every(k => context[k] === null || typeof context[k] === 'string')) fail()
  for (const key of ['services', 'people', 'active', 'entries', 'corrections']) if (!Array.isArray(data[key])) fail()
  for (const row of data.services) if (!only(row, ['ref', 'name']) || typeof row.ref !== 'string' || typeof row.name !== 'string') fail()
  for (const row of data.people) if (!only(row, ['id', 'name']) || !isTimeUuid(row.id) || (!owner && row.id !== actorId)) fail()
  for (const [key, extra] of [['active', ['stopped_at']], ['entries', ['ended_at', 'seconds', 'state', 'source', 'session_id', 'reason', 'recorded_at', 'superseded']]]) {
    const ids = new Set()
    for (const row of data[key]) {
      if (!only(row, [...common, ...extra]) || !isTimeUuid(row.id) || ids.has(row.id) || row.workshop_id !== workshopId
        || (key === 'entries' && row.job_id !== jobId) || (!owner && row.technician_id !== actorId)
        || !timeActivities[row.activity] || !stamp(row.started_at)) fail()
      if (key === 'active' && (row.stopped_at !== null || row.technician_id !== actorId)) fail()
      if (key === 'entries' && (!stamp(row.ended_at) || !Number.isSafeInteger(row.seconds) || row.seconds < 0
        || !['final', 'pending_review'].includes(row.state) || typeof row.superseded !== 'boolean')) fail()
      ids.add(row.id)
    }
  }
  for (const row of data.corrections) if (!only(row, ['id', 'entry_id', 'replacements', 'actor_id', 'reason', 'recorded_at'])
    || !data.entries.some(e => e.id === row.entry_id) || !Array.isArray(row.replacements) || !row.replacements.every(isTimeUuid)) fail()
  return data
}

// Explicit numeric offset resolves the repeated London hour; never infer browser timezone.
export function anchoredInterval(start, minutes, activity, serviceRef = null) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/.test(start) || !stamp(start)
    || !Number.isFinite(Number(minutes)) || Number(minutes) <= 0 || Number(minutes) > 960 || !timeActivities[activity]) throw new Error('Use an ISO start with UTC offset and 0–960 positive minutes.')
  const seconds = Number(minutes) * 60
  if (!Number.isInteger(seconds)) throw new Error('Use whole seconds.')
  const began = new Date(start), end = new Date(began.getTime() + seconds * 1000)
  const offset = start.endsWith('Z') ? 0 : (start.slice(-6, -5) === '-' ? -1 : 1) * (Number(start.slice(-5, -3)) * 60 + Number(start.slice(-2)))
  if (new Date(began.getTime() + offset * 60000).toISOString().slice(0, 19) !== start.slice(0, 19)) throw new Error('Choose a real calendar date.')
  if (end.getTime() > Date.now()) throw new Error('Manual time must end in the past.')
  return { started_at: began.toISOString(), ended_at: end.toISOString(), activity, service_ref: serviceRef || null }
}

export const finalizedSeconds = entries => entries.filter(e => !e.superseded && e.state === 'final').reduce((sum, e) => sum + e.seconds, 0)

export function londonStartChoices(local) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(local || '')) return []
  const full = local.length === 16 ? `${local}:00` : local
  const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
  return [60, 0].flatMap(offset => {
    const date = new Date(Date.parse(full + 'Z') - offset * 60000)
    if (!Number.isFinite(date.getTime())) return []
    const parts = Object.fromEntries(formatter.formatToParts(date).map(p => [p.type, p.value]))
    const wall = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
    return wall === full ? [{ value: date.toISOString(), label: offset ? 'First occurrence · BST (UTC+1)' : 'GMT (UTC+0)' }] : []
  })
}

export function londonStart(local, selected) {
  const choices = londonStartChoices(local)
  if (choices.length === 1) return choices[0].value.replace('.000', '')
  if (choices.length === 2 && choices.some(c => c.value === selected)) return selected.replace('.000', '')
  throw new Error(choices.length ? 'This clock time occurs twice. Choose BST or GMT.' : 'Choose a valid London date and time; the spring clock-change gap is unavailable.')
}
